import React from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Stack, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from "@mui/material/styles";
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { IUserState } from '../../Core/State/IUserState';
import styles from '../../assets/css/components/MobileLobby.module.css';
import icon from '/images/icon.svg';
import { buildDepositReturnUrl, createDepositSession } from '../../Casino/CasinoDeposit';
import type { CreateDepositSessionInput, DepositSessionResult } from '../../Casino/Type/CasinoTypes';
import { getCurrentLanguage, t } from '../../i18n';
import { canShowGlobalChat } from '../../Chat/ChatAccessControl';
import CommunityChatPanel from '../CommunityChat/CommunityChatPanel';
import DepositCashierModal from './DepositCashierModal';

interface MainProps {
    userState: IUserState;
    logout: () => void;
    section: string;
}

interface HeaderState {
    anchorEl: HTMLElement | null;
    previousBalance: string | null;
    isBalanceUpdated: boolean;
    isChatOpen: boolean;
    isDepositModalOpen: boolean;
    isDepositSessionLoading: boolean;
    depositSession: DepositSessionResult | null;
    depositError: string | null;
}

export default class MobileHeader extends React.Component<MainProps, HeaderState> {

    private depositSessionAbortController: AbortController | null = null;

    constructor(props: MainProps) {
        super(props);
        this.state = {
            anchorEl: null,
            previousBalance: null,
            isBalanceUpdated: false,
            isChatOpen: false,
            isDepositModalOpen: false,
            isDepositSessionLoading: false,
            depositSession: null,
            depositError: null
        };
    }

    darkTheme = createTheme({
        palette: {
            mode: "dark",
            primary: {
                main: "#F93751"
            }
        },
        typography: {
            fontFamily: 'Inter, serif',
        },
    });

    componentDidUpdate(prevProps: MainProps) {
        if (prevProps.userState.balance !== this.props.userState.balance) {
            this.setState({
                isBalanceUpdated: true,
                previousBalance: prevProps.userState.balance.toString()
            });

            setTimeout(() => {
                this.setState({ isBalanceUpdated: false });
            }, 1000);
        }
    }

    componentWillUnmount() {
        this.depositSessionAbortController?.abort();
    }

    private handleChatOpen = () => {
        this.setState({ isChatOpen: true });
    };

    private handleChatClose = () => {
        this.setState({ isChatOpen: false });
    };

    private buildDepositSessionInput = (): CreateDepositSessionInput | undefined => {
        const input: CreateDepositSessionInput = {};
        const language = getCurrentLanguage();
        const returnUrl = buildDepositReturnUrl();

        if (language) input.language = language;
        if (returnUrl) input.returnUrl = returnUrl;

        return Object.keys(input).length > 0 ? input : undefined;
    };

    private handleDepositSessionClick = async () => {
        if (this.state.isDepositSessionLoading) return;

        this.depositSessionAbortController?.abort();
        const controller = new AbortController();
        this.depositSessionAbortController = controller;

        this.setState({
            isDepositModalOpen: true,
            isDepositSessionLoading: true,
            depositSession: null,
            depositError: null
        });

        try {
            const session = await createDepositSession({
                input: this.buildDepositSessionInput(),
                signal: controller.signal
            });

            if (controller.signal.aborted) return;

            this.setState({
                isDepositSessionLoading: false,
                depositSession: session,
                depositError: session.cashierUrl ? null : t('DesktopHeader:depositFlow.unavailable')
            });
        } catch (err) {
            if (controller.signal.aborted) return;

            this.setState({
                isDepositSessionLoading: false,
                depositSession: null,
                depositError: err instanceof Error && err.message ? err.message : t('DesktopHeader:depositFlow.error')
            });
        } finally {
            if (this.depositSessionAbortController === controller) {
                this.depositSessionAbortController = null;
            }
        }
    };

    private handleDepositModalClose = () => {
        this.depositSessionAbortController?.abort();
        this.depositSessionAbortController = null;
        this.setState({
            isDepositModalOpen: false,
            isDepositSessionLoading: false,
            depositSession: null,
            depositError: null
        });
    };

    render() {
        const user = this.props.userState;
        const { section } = this.props;
        const balance = user.balance ? Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(user.balance) : '0.00';
        const canUseGlobalChat = Boolean(user.authenticated && canShowGlobalChat(user.username));
        const chatTitle = 'Community chat';

        return (
            <ThemeProvider theme={this.darkTheme}>
                { (section !== "poker_single_room_view" && section !== "poker_grid_room_view") && (
                    <>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" className={styles.mobileLobbyHeader} alignItems="center">
                                <img src={icon} alt="PokerSolutions Icon" height={28} style={{ marginLeft: '0.75rem' }} />
                            </Stack>
                            {user.authenticated && (
                                <Stack direction='row' alignItems='center' spacing={1} sx={{ pr: '15px' }}>
                                    <Typography
                                        className={`${styles.mobileLobbyBalance} ${this.state.isBalanceUpdated ? styles.balanceUpdated : ''}`}
                                        component="p"
                                        variant="body1"
                                        style={{ margin: '15px 0 15px 15px' }}
                                    >
                                        ${balance}
                                    </Typography>
                                    {canUseGlobalChat && (
                                        <IconButton
                                            aria-label="Open community chat"
                                            onClick={this.handleChatOpen}
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                color: '#FFFFFF',
                                                backgroundColor: '#22242C',
                                                border: '1px solid rgba(255,255,255,0.12)',
                                                '&:hover': {
                                                    backgroundColor: '#2B2D36'
                                                }
                                            }}
                                        >
                                            <ChatBubbleOutlineIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    <Button
                                        variant="contained"
                                        disabled={this.state.isDepositSessionLoading}
                                        onClick={() => { void this.handleDepositSessionClick(); }}
                                        sx={{
                                            backgroundColor: '#F52740',
                                            color: '#FFFFFF',
                                            fontSize: '14px',
                                            fontWeight: 800,
                                            textTransform: 'none',
                                            padding: '.75rem 1rem',
                                            borderRadius: '6px',
                                            marginRight: 0,
                                            minWidth: 96,
                                            '&:hover': {
                                                backgroundColor: '#F52740'
                                            }
                                        }}
                                    >
                                        {this.state.isDepositSessionLoading ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            'Deposit'
                                        )}
                                    </Button>
                                </Stack>
                            )}
                        </Stack>
                        <Dialog
                            fullScreen
                            open={this.state.isChatOpen && canUseGlobalChat}
                            onClose={this.handleChatClose}
                            PaperProps={{
                                sx: {
                                    bgcolor: '#191A1F',
                                    color: '#FFFFFF',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100dvh',
                                    overflow: 'hidden',
                                    position: 'relative'
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    flex: '1 1 auto',
                                    minHeight: 0,
                                    bgcolor: '#191A1F'
                                }}
                            >
                                <CommunityChatPanel
                                    active={this.state.isChatOpen && canUseGlobalChat}
                                    title={chatTitle}
                                    onClose={this.handleChatClose}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        minHeight: 0,
                                        border: 0,
                                        borderRadius: 0,
                                        boxShadow: 'none',
                                        '@media (max-width: 600px)': {
                                            width: '100%',
                                            height: '100%',
                                            minHeight: 0,
                                            borderRadius: 0
                                        }
                                    }}
                                />
                            </Box>
                            <IconButton
                                aria-label={t('common:actions.close')}
                                onClick={this.handleChatClose}
                                sx={{
                                    position: 'absolute',
                                    right: 12,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 2,
                                    width: 40,
                                    height: 40,
                                    color: '#FFFFFF',
                                    bgcolor: 'rgba(34,36,44,0.92)',
                                    border: '1px solid rgba(255,255,255,0.16)',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                                    '&:hover': {
                                        bgcolor: 'rgba(43,45,54,0.98)'
                                    }
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Dialog>
                    </>
                )}
                <DepositCashierModal
                    open={this.state.isDepositModalOpen}
                    loading={this.state.isDepositSessionLoading}
                    error={this.state.depositError}
                    session={this.state.depositSession}
                    onClose={this.handleDepositModalClose}
                />
            </ThemeProvider>
        );
    }
};
